import { useMemo } from "react";

import { Client } from "@/Common/Api/Client";
import { EmployeeTermsAndConditionsAgreement } from "@/Common/Api/Employee/Dto/EmployeeTermsAndConditionsAgreement";
import { ServiceTermsAndConditionsInfo } from "@/Common/Api/Employee/Dto/ServiceTermsAndConditionsInfo";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { TermsAndConditionsServiceNames } from "@/Common/Resources/Lookups/Enums/TermsAndConditionsServiceNames";
import { ButtonControl } from "../Buttons/ButtonControl";
import ButtonGroup from "../Buttons/ButtonGroup";
import { Modal } from "../Modals/Modal";

import "./TermsAndConditionsPopup.scss";

interface ITermsAndConditionsPopupProps {
    serviceName: TermsAndConditionsServiceNames;
    onClose: () => void;
    onOk?: () => void;
}

function TermsAndConditionsPopup(props: Readonly<ITermsAndConditionsPopupProps>): JSX.Element {
    const { getText } = useTranslation();

    const serviceTermsAndConditionInfo: ServiceTermsAndConditionsInfo | null = useMemo(
        () => cacheStore.serviceTermsAndConditionsDictionary[props.serviceName],
        [props.serviceName]
    );

    async function handleOnOk(): Promise<void> {
        const result = await Client.postAsync<{ TermsAndConditionsAgreements: EmployeeTermsAndConditionsAgreement[] }>(
            ServerOperations.Server.Myle_AgreeToTermsAndConditions,
            { ServiceName: props.serviceName }
        );

        if (result.isSuccess)
            cacheStore.setEmployeeTermsAndConditionsAgreement(result.data.TermsAndConditionsAgreements);

        props?.onOk();
        props.onClose();
    }

    function renderFooter(): JSX.Element {
        return (
            <div className="flex-container flex-end">
                {serviceTermsAndConditionInfo && (
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => props.onClose()}
                            label={getText("CancelLabel")}
                        />
                        <ButtonControl
                            variant="brand"
                            onClick={handleOnOk}
                            label={getText("ContinueLabel")}
                        />
                    </ButtonGroup>
                )}
            </div>
        );
    }

    return (
        <Modal
            cssClass="terms-and-conditions-popup-title"
            width={450}
            draggable={false}
            title={getText("TermsAndConditions_Label")}
            disableOverlayClose
            centerContent
            showOverlay
            showX
            onClose={() => props.onClose()}
            footer={renderFooter()}
        >
            <div className="myle-form">
                <p className="row">
                    {!serviceTermsAndConditionInfo
                        ? getText("TermsAndConditions_NotFound")
                        : (
                            <>
                                <p className="row">{getText(serviceTermsAndConditionInfo.AgreementVocabKey)}</p>
                                <p>
                                    {getText("TermsAndConditions_AgreementLabel")}
                                    <a target="_blank" className="highlight" href={serviceTermsAndConditionInfo.AgreeResourceUrl}>
                                        {getText("TermsAndConditions_Label").toLocaleLowerCase()}.
                                    </a>
                                </p>
                            </>
                        )
                    }
                </p>
            </div>
        </Modal>
    );
}

export { TermsAndConditionsPopup };
